/**
 * Sync engine (Phase 16C C4, design §6.6 / §8.6). Foreground only (decision X5): the outbox is
 * encrypted with a key that exists only in page memory, so nothing syncs with the app closed.
 *
 * - Triggers: app open / unlock (start), `online`, back to the foreground, after each enqueue
 *   (debounced 2 s), and every 30 s while visible with work pending.
 * - Single flight across tabs (`pelora-sync` lock).
 * - Batches: grouped by visit, in `seq` order, at most 25 ops or 4 MB; an op never goes before an
 *   unapplied `dependsOn` (unless that op is in the same batch, earlier).
 * - Results (§8.6): applied / duplicate → op and escrow removed, local copy marked synced;
 *   conflict → Sync Center with the server's `current`; needs_attention → shown with the code,
 *   DEPENDENCY_FAILED retried automatically; quarantined → "Held for supervisor review".
 * - Request errors: network / 5xx / 408 / 409 / 429 → backoff 2 s → 5 min with jitter; 401 → one
 *   refresh, then ask to unlock / sign in; 402 → keep the queue (account banner); 403
 *   OFFLINE_DEVICE_REVOKED → upload the sealed copies, then wipe.
 * - Heartbeat after each flush with counts and ids only.
 */
import { isApiError } from '../../dtos/common/api.dto';
import type { DeviceHeartbeatResponse, OfflineSettingsDto } from '../../dtos/offline/offline.dto';
import type { SealedSyncRequestDto, SyncOpDto, SyncOpResultDto, SyncRequestDto, SyncResponseDto } from '../../dtos/offline/sync.dto';
import { SYNC_CODES } from '../../dtos/offline/sync.dto';
import { isUnsynced, Outbox, OutboxOp } from './outbox';
import type { OfflineRuntime } from './runtime';
import { defaultLock, LockFn } from './syncLock';
import type { VisitRepo, SyncState as EntitySyncState } from './repos/visitRepo';
import type { ChartRepo } from './repos/chartRepo';
import type { AssessmentRepo } from './repos/assessmentRepo';
import type { BlobRepo } from './repos/blobRepo';

export const MAX_BATCH_OPS = 25;
export const MAX_BATCH_BYTES = 4 * 1024 * 1024;
export const BACKOFF_MIN_MS = 2_000;
export const BACKOFF_MAX_MS = 5 * 60_000;
const ENQUEUE_DEBOUNCE_MS = 2_000;
const POLL_MS = 30_000;
const HEARTBEAT_MIN_MS = 5 * 60_000;
const MAX_ROUNDS = 20;

export interface HeartbeatInput {
  deviceId: string;
  pendingOps: number;
  oldestPendingAt?: string;
  pendingVisitIds: string[];
  pendingShiftIds: string[];
}

export interface SyncTransport {
  sync(body: SyncRequestDto | SealedSyncRequestDto, headers: Record<string, string>): Promise<SyncResponseDto>;
  heartbeat(input: HeartbeatInput): Promise<DeviceHeartbeatResponse>;
}

export type WipeReason = 'revoked' | 'downgrade';
export type SyncBlock = 'auth' | 'account' | 'device' | null;

export interface SyncEngineState {
  syncing: boolean;
  blocked: SyncBlock;
  failures: number;
  backoffUntil?: number;
  lastSyncAt?: string;
  lastError?: string;
}

export interface SyncEngineDeps {
  rt: OfflineRuntime;
  outbox: Outbox;
  visits: VisitRepo;
  charts: ChartRepo;
  assessments: AssessmentRepo;
  blobs: BlobRepo;
  transport: SyncTransport;
  lock?: LockFn;
  online?: () => boolean;
  /** One silent token refresh after a 401 (apiClient already retries once; tests inject this). */
  refreshAuth?: () => Promise<boolean>;
  /** Erase this user's offline data (after the sealed upload). */
  onWipe?: (reason: WipeReason) => Promise<void>;
  onSettings?: (settings: OfflineSettingsDto) => void;
  random?: () => number;
}

const resultCode = (r: SyncOpResultDto): string | undefined =>
  typeof r.error === 'string' ? r.error : r.error?.code;
const resultMessage = (r: SyncOpResultDto): string | undefined =>
  typeof r.error === 'string' ? undefined : r.error?.message;

const UPDATE_TYPES = new Set(['CHART_UPDATE', 'ASSESSMENT_UPDATE']);

/** The next expectedVersion after an applied op (16B editVersion; `version` only as a fallback). */
const editVersionOf = (r?: SyncOpResultDto): number | undefined => {
  if (!r) return undefined;
  if (typeof r.editVersion === 'number') return r.editVersion;
  const cur = r.current && typeof r.current.editVersion === 'number' ? r.current.editVersion : undefined;
  return cur ?? r.version;
};

type BatchOutcome = 'progress' | 'stalled' | 'stop' | 'retry';

export class SyncEngine {
  private state: SyncEngineState = { syncing: false, blocked: null, failures: 0 };
  private listeners = new Set<() => void>();
  private lock: LockFn;
  private online: () => boolean;
  private random: () => number;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private backoffTimer: ReturnType<typeof setTimeout> | null = null;
  private poll: ReturnType<typeof setInterval> | null = null;
  private detach: Array<() => void> = [];
  private refreshedThisFlush = false;
  private pendingWipe: WipeReason | null = null;
  private wiping = false;
  private lastBeat = 0;
  private lastBeatSig = '';

  constructor(private readonly deps: SyncEngineDeps) {
    this.lock = deps.lock || defaultLock;
    this.online = deps.online || (() => typeof navigator === 'undefined' || navigator.onLine !== false);
    this.random = deps.random || Math.random;
  }

  // ---- observable state ----

  getState(): SyncEngineState { return this.state; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private set(patch: Partial<SyncEngineState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  // ---- triggers ----

  start(): void {
    if (this.detach.length) return;
    const onOnline = () => this.requestSync(0);
    const onVisible = () => { if (typeof document === 'undefined' || document.visibilityState === 'visible') this.requestSync(0); };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    const unsub = this.deps.outbox.subscribe(() => { if (!this.state.syncing) this.requestSync(ENQUEUE_DEBOUNCE_MS); });
    this.poll = setInterval(async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        if ((await this.deps.outbox.counts()).pending > 0) this.flush();
      } catch {
        // locked: nothing to do
      }
    }, POLL_MS);
    this.detach = [
      () => window.removeEventListener('online', onOnline),
      () => document.removeEventListener('visibilitychange', onVisible),
      unsub,
    ];
    this.requestSync(0);
  }

  stop(): void {
    this.detach.forEach((d) => d());
    this.detach = [];
    if (this.poll) clearInterval(this.poll);
    if (this.debounce) clearTimeout(this.debounce);
    if (this.backoffTimer) clearTimeout(this.backoffTimer);
    this.poll = null;
    this.debounce = null;
    this.backoffTimer = null;
  }

  requestSync(delayMs = ENQUEUE_DEBOUNCE_MS): void {
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => {
      this.debounce = null;
      this.flush().catch(() => undefined);
    }, delayMs);
  }

  /** Clear backoff and blocks (Sync Center "Retry", unlock after sign-in). */
  async retryNow(): Promise<void> {
    this.set({ backoffUntil: undefined, failures: 0, blocked: null });
    await this.flush();
  }

  // ---- flush ----

  async flush(): Promise<void> {
    if (!this.online()) return;
    if (this.state.backoffUntil && Date.now() < this.state.backoffUntil) return;
    this.refreshedThisFlush = false;
    await this.lock('pelora-sync', () => this.runFlush());
    if (this.pendingWipe && !this.wiping) await this.syncThenWipe(this.pendingWipe);
  }

  private async runFlush(): Promise<void> {
    this.set({ syncing: true });
    try {
      for (let round = 0; round < MAX_ROUNDS; round++) {
        const ops = await this.deps.outbox.list();
        const batch = await this.selectBatch(ops);
        if (!batch.length) break;
        let outcome = await this.sendBatch(batch);
        if (outcome === 'retry') outcome = await this.sendBatch(batch);
        if (outcome !== 'progress') break;
      }
      if (!this.pendingWipe) await this.heartbeat();
    } finally {
      this.set({ syncing: false });
    }
  }

  /** Grouped by visit (visits in order of their first op), `seq` order, dependency-safe, size-capped. */
  async selectBatch(ops: OutboxOp[]): Promise<Array<{ op: OutboxOp; wire: SyncOpDto }>> {
    const present = new Set(ops.filter(isUnsynced).map((o) => o.opId));
    const byVisit = new Map<string, OutboxOp[]>();
    for (const op of [...ops].sort((a, b) => a.seq - b.seq)) {
      const list = byVisit.get(op.visitId) || [];
      list.push(op);
      byVisit.set(op.visitId, list);
    }
    const chosen: Array<{ op: OutboxOp; wire: SyncOpDto }> = [];
    const chosenIds = new Set<string>();
    let bytes = 0;
    for (const visitOps of byVisit.values()) {
      for (const op of visitOps) {
        if (op.status !== 'pending') continue;
        if (op.dependsOn && present.has(op.dependsOn) && !chosenIds.has(op.dependsOn)) continue;
        const wire = await this.deps.outbox.toWire(op, UPDATE_TYPES.has(op.type) ? await this.entityVersion(op) : undefined);
        const size = JSON.stringify(wire).length;
        if (chosen.length >= MAX_BATCH_OPS || (chosen.length > 0 && bytes + size > MAX_BATCH_BYTES)) return chosen;
        chosen.push({ op, wire });
        chosenIds.add(op.opId);
        bytes += size;
      }
    }
    return chosen;
  }

  /** expectedVersion for an update queued before its create was confirmed. */
  private async entityVersion(op: OutboxOp): Promise<number | undefined> {
    if (!op.entityId) return undefined;
    if (op.type === 'CHART_UPDATE') return (await this.deps.charts.get(op.entityId))?.serverVersion;
    return (await this.deps.assessments.get(op.entityId))?.serverVersion;
  }

  /** 16B: device id in the body; X-Device-Class and X-Pending-Ops as headers. */
  private async headers(): Promise<Record<string, string>> {
    const counts = await this.deps.outbox.counts();
    return {
      'X-Device-Class': 'MOBILE',
      'X-Pending-Ops': String(counts.pending),
    };
  }

  private async sendBatch(batch: Array<{ op: OutboxOp; wire: SyncOpDto }>): Promise<BatchOutcome> {
    let response: SyncResponseDto;
    try {
      response = await this.deps.transport.sync({ deviceId: this.deps.rt.identity().deviceId, ops: batch.map((b) => b.wire) }, await this.headers());
    } catch (error) {
      return this.requestError(error, batch.map((b) => b.op));
    }
    this.set({ failures: 0, backoffUntil: undefined, blocked: null, lastError: undefined, lastSyncAt: new Date(this.deps.rt.now()).toISOString() });
    if (response?.serverTime) {
      await this.deps.outbox.setTimeAnchor({ serverAt: response.serverTime, deviceAt: new Date().toISOString() }).catch(() => undefined);
    }
    if (response?.revoked) this.pendingWipe = 'revoked';
    const deferred = new Set(response?.deferred || []);
    const results = new Map((response?.results || []).map((r) => [r.opId, r]));
    let progressed = false;
    const touched: OutboxOp[] = [];
    for (const { op } of batch) {
      const result = deferred.has(op.opId) ? undefined : results.get(op.opId);
      if (!result) {
        // Not processed (server time budget): resend later
        await this.deps.outbox.update(op.opId, { status: 'pending', attempts: op.attempts + 1 });
        continue;
      }
      if (await this.applyResult(op, result)) progressed = true;
      touched.push(op);
    }
    await this.reconcile(touched, results);
    if (this.pendingWipe) return 'stop';
    return progressed ? 'progress' : 'stalled';
  }

  /** @returns true when the op left the outbox (applied / duplicate). */
  private async applyResult(op: OutboxOp, r: SyncOpResultDto): Promise<boolean> {
    const code = resultCode(r);
    const field = typeof r.error === 'object' && r.error?.field ? ` (${r.error.field})` : '';
    if (r.quarantined || r.status === 'quarantined') {
      // Held server-side for supervisor import (TIME_TOO_OLD / TIME_IN_FUTURE / unassigned)
      await this.deps.outbox.update(op.opId, { status: 'needs_attention', errorCode: SYNC_CODES.QUARANTINED, lastError: code && code !== SYNC_CODES.QUARANTINED ? code : undefined, attempts: op.attempts + 1 });
      return false;
    }
    if (r.status === 'applied' || r.status === 'duplicate') {
      await this.deps.outbox.remove(op.opId);
      return true;
    }
    if (r.status === 'conflict') {
      await this.deps.outbox.update(op.opId, { status: 'conflict', errorCode: code || 'CONFLICT', lastError: resultMessage(r), current: r.current ?? null, attempts: op.attempts + 1 });
      return false;
    }
    if (code === SYNC_CODES.DEPENDENCY_FAILED) {
      await this.deps.outbox.update(op.opId, { status: 'pending', errorCode: code, attempts: op.attempts + 1 });
      return false;
    }
    const message = resultMessage(r);
    await this.deps.outbox.update(op.opId, { status: 'needs_attention', errorCode: code || SYNC_CODES.VALIDATION, lastError: message || field ? `${message || ''}${field}`.trim() : undefined, attempts: op.attempts + 1 });
    return false;
  }

  /** Local copies follow their ops: synced once no unsynced op for the entity / visit remains. */
  private async reconcile(touched: OutboxOp[], results: Map<string, SyncOpResultDto>): Promise<void> {
    const remaining = (await this.deps.outbox.list()).filter(isUnsynced);
    const stateFor = (ops: OutboxOp[]): EntitySyncState => {
      if (!ops.length) return 'synced';
      if (ops.some((o) => o.status === 'conflict')) return 'conflict';
      if (ops.some((o) => o.status === 'needs_attention')) return 'needs_attention';
      return 'pending';
    };
    for (const op of touched) {
      const r = results.get(op.opId);
      const applied = r?.status === 'applied' || r?.status === 'duplicate';
      try {
        if (op.entityId && op.type.startsWith('CHART_') && op.type !== 'CHART_SUBMIT') {
          const left = remaining.filter((o) => o.entityId === op.entityId);
          if (!left.length && applied) await this.deps.charts.markSynced(op.entityId, editVersionOf(r));
          else await this.deps.charts.setSyncState(op.entityId, stateFor(left));
        } else if (op.entityId && op.type.startsWith('ASSESSMENT_')) {
          const left = remaining.filter((o) => o.entityId === op.entityId);
          if (!left.length && applied) await this.deps.assessments.markSynced(op.entityId, editVersionOf(r));
          else await this.deps.assessments.setSyncState(op.entityId, stateFor(left));
        }
        if (applied && op.type === 'VISIT_END') {
          const blobId = typeof op.payload.signatureBlobId === 'string' ? op.payload.signatureBlobId : null;
          if (blobId) await this.deps.blobs.delete(blobId);
        }
        const leftForVisit = remaining.filter((o) => o.visitId === op.visitId);
        if (!leftForVisit.length) {
          await this.deps.visits.markSynced(op.visitId, r?.syncFlags ? { syncFlags: r.syncFlags } : {});
        } else if (r?.syncFlags?.length) {
          const visit = await this.deps.visits.get(op.visitId);
          if (visit) await this.deps.visits.put({ ...visit, syncFlags: r.syncFlags });
        }
      } catch {
        // the local copy is a cache; the outbox is the source of truth
      }
    }
  }

  private async requestError(error: unknown, ops: OutboxOp[]): Promise<BatchOutcome> {
    const status = isApiError(error) ? error.statusCode : 0;
    const domain = isApiError(error) ? error.domainCode : undefined;
    if (status === 401) {
      if (this.deps.refreshAuth && !this.refreshedThisFlush) {
        this.refreshedThisFlush = true;
        if (await this.deps.refreshAuth().catch(() => false)) return 'retry';
      }
      this.set({ blocked: 'auth', lastError: 'Sign in again to sync' });
      return 'stop';
    }
    if (status === 402) {
      this.set({ blocked: 'account', lastError: 'Your agency account is read-only. Work stays on this device.' });
      return 'stop';
    }
    if (status === 409) {
      // OP_IN_FLIGHT: an earlier attempt is still being applied; retry later
      this.backoff();
      return 'stop';
    }
    if (status === 403 && domain === 'OFFLINE_DEVICE_REVOKED') {
      this.pendingWipe = 'revoked';
      return 'stop';
    }
    if (status === 403) {
      this.set({ blocked: 'device', lastError: 'This device can\'t sync. Ask your administrator.' });
      return 'stop';
    }
    if (status === 400 || status === 413) {
      const message = isApiError(error) ? error.message : 'The server refused this batch';
      for (const op of ops) await this.deps.outbox.update(op.opId, { status: 'needs_attention', errorCode: domain || SYNC_CODES.VALIDATION, lastError: message, attempts: op.attempts + 1 });
      return 'stop';
    }
    this.backoff();
    return 'stop';
  }

  private backoff(): void {
    const failures = this.state.failures + 1;
    const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** (failures - 1));
    const delay = Math.round(base * (0.5 + this.random() * 0.5));
    this.set({ failures, backoffUntil: Date.now() + delay, lastError: 'Waiting for a connection' });
    if (this.backoffTimer) clearTimeout(this.backoffTimer);
    if (this.detach.length) this.backoffTimer = setTimeout(() => { this.backoffTimer = null; this.flush().catch(() => undefined); }, delay);
  }

  // ---- heartbeat ----

  async heartbeat(force = false): Promise<void> {
    let counts;
    try {
      counts = await this.deps.outbox.counts();
    } catch {
      return;
    }
    const sig = `${counts.pending}|${counts.oldestPendingAt || ''}`;
    if (!force && sig === this.lastBeatSig && Date.now() - this.lastBeat < HEARTBEAT_MIN_MS) return;
    try {
      const result = await this.deps.transport.heartbeat({
        deviceId: this.deps.rt.identity().deviceId,
        pendingOps: counts.pending,
        oldestPendingAt: counts.oldestPendingAt,
        pendingVisitIds: counts.visitIds.slice(0, 50),
        pendingShiftIds: counts.shiftIds.slice(0, 50),
      });
      this.lastBeat = Date.now();
      this.lastBeatSig = sig;
      if (result?.revoked) this.pendingWipe = 'revoked';
      else if (result?.offlineMode === false) this.pendingWipe = 'downgrade';
      else if (result?.offlineSettings) this.deps.onSettings?.(result.offlineSettings);
    } catch {
      // offline or transient: the next flush beats again
    }
  }

  /** Heartbeat now and act on revoke / downgrade (OfflineGate's hourly check). */
  async checkDevice(): Promise<void> {
    await this.heartbeat(true);
    if (this.pendingWipe && !this.wiping) await this.syncThenWipe(this.pendingWipe);
  }

  // ---- revoke / downgrade ----

  /** Upload every sealed copy; true when none is left. */
  async uploadSealed(): Promise<boolean> {
    const sealed = await this.deps.outbox.sealed();
    for (let i = 0; i < sealed.length; i += MAX_BATCH_OPS) {
      const chunk = sealed.slice(i, i + MAX_BATCH_OPS);
      let response: SyncResponseDto;
      try {
        response = await this.deps.transport.sync({ deviceId: this.deps.rt.identity().deviceId, sealed: true, ops: chunk }, await this.headers());
      } catch {
        return false;
      }
      for (const r of response?.results || []) {
        if (r.status === 'applied' || r.status === 'duplicate' || r.quarantined || r.status === 'quarantined') {
          await this.deps.outbox.removeSealed(r.opId);
        }
      }
    }
    return (await this.deps.outbox.sealed()).length === 0;
  }

  /**
   * Design §7.4 / plan C12: sync first (plaintext when the server still takes it, i.e. a
   * downgrade), hand over the sealed copies, then wipe. Nothing is wiped while the upload fails.
   */
  async syncThenWipe(reason: WipeReason): Promise<boolean> {
    if (this.wiping) return false;
    this.wiping = true;
    try {
      if (reason === 'downgrade') {
        this.pendingWipe = null;
        await this.lock('pelora-sync', () => this.runFlushNoBeat());
      }
      let uploaded = false;
      await this.lock('pelora-sync', async () => { uploaded = await this.uploadSealed(); });
      if (!uploaded) {
        this.pendingWipe = reason;
        return false;
      }
      this.pendingWipe = null;
      this.stop();
      await this.deps.onWipe?.(reason);
      return true;
    } finally {
      this.wiping = false;
    }
  }

  private async runFlushNoBeat(): Promise<void> {
    for (let round = 0; round < MAX_ROUNDS; round++) {
      const batch = await this.selectBatch(await this.deps.outbox.list());
      if (!batch.length) return;
      if ((await this.sendBatch(batch)) !== 'progress') return;
    }
  }
}
